import type { IMovementRepository, IProductRepository } from '../../domain/interfaces';
import type { SaleResponseDTO, SaleItemResponseDTO } from '../dtos/sale/SaleResponseDTO';

export interface FindSalesUseCase {
  execute(filters?: {
    warehouseId?: string;
    startDate?: Date;
    endDate?: Date;
  }): Promise<SaleResponseDTO[]>;
}

export default class FindSalesUseCaseImpl implements FindSalesUseCase {
  constructor(
    private readonly movementRepository: IMovementRepository,
    private readonly productRepository: IProductRepository
  ) {}

  async execute(filters?: {
    warehouseId?: string;
    startDate?: Date;
    endDate?: Date;
  }): Promise<SaleResponseDTO[]> {
    try {
      // Query all OUT movements (sales)
      const movementEntities = await this.movementRepository.findAll();
      
      // Convert entities to JSON for easier property access
      const movements = movementEntities.map(m => m.toJSON());
      
      // Filter movements by type OUT and optional filters
      let salesMovements = movements.filter((m) => m.movementType === 'OUT');

      if (filters?.warehouseId) {
        salesMovements = salesMovements.filter(
          (m) => m.warehouseId === filters.warehouseId
        );
      }

      if (filters?.startDate) {
        salesMovements = salesMovements.filter(
          (m) => new Date(m.createdAt) >= filters.startDate!
        );
      }

      if (filters?.endDate) {
        salesMovements = salesMovements.filter(
          (m) => new Date(m.createdAt) <= filters.endDate!
        );
      }

      // Group movements by structured reference number
      const salesMap = new Map<string, typeof salesMovements>();

      for (const movement of salesMovements) {
        // Use structured reference field (SO = Sales Order)
        const ref = movement.reference?.type === 'SO'
          ? movement.reference.number
          : movement._id; // Fallback for legacy movements without structured reference

        if (!salesMap.has(ref)) {
          salesMap.set(ref, []);
        }
        salesMap.get(ref)!.push(movement);
      }

      // Build sale response DTOs from grouped movements
      const sales: SaleResponseDTO[] = [];

      // Use Array.from for Map iteration compatibility
      for (const [referenceNumber, movementGroup] of Array.from(salesMap.entries())) {
        // Get product details for each movement
        const items: SaleItemResponseDTO[] = await Promise.all(
          movementGroup.map(async (movement) => {
            const product = await this.productRepository.findById(movement.productId);
            return {
              productId: movement.productId,
              productName: product?.name || 'Unknown Product',
              productSku: product?.sku || 'N/A',
              quantity: movement.quantity.value,
              unitPrice: movement.unitPrice?.amount || 0
            };
          })
        );

        const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
        const totalAmount = items.reduce(
          (sum, item) => sum + item.quantity * item.unitPrice,
          0
        );

        // Use the first movement's data for sale-level info
        const firstMovement = movementGroup[0];

        sales.push({
          referenceNumber,
          date: firstMovement.createdAt,
          warehouseId: firstMovement.warehouseId,
          items,
          totalItems,
          totalAmount,
          status: 'completed',
          movementIds: movementGroup.map((m) => m._id),
          createdAt: firstMovement.createdAt
        });
      }

      // Sort by date descending (newest first)
      sales.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      return sales;
    } catch (error) {
      throw new Error(
        `Failed to find sales: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }
}
